import React, { useEffect, useMemo, useRef } from "react";
import {
  isRelatedQuestionAction,
  Message,
  MessageAction,
  Research,
} from "../../adapters/insights-agent/state/AppState.ts";
import { Card } from "../../ui-library/Card/Card.tsx";
import { LoadingDots } from "../../ui-library/LoadingDots/LoadingDots.tsx";
import { LoadingCard } from "../LoadingCard/LoadingCard.tsx";
import { ActionBar } from "./ActionBar.tsx";
import styles from "./ChatMessages.module.css";
import { MessageBubble } from "./MessageBubble.tsx";
import {
  CitationComponent,
  MarkdownWithCitations,
} from "../MarkdownWithCitations/MarkdownWithCitations.tsx";
import { Citation } from "../MarkdownWithCitations/Citation.tsx";
import { AppliedFilters } from "./AppliedFilters.tsx";
import { RelatedQuestions } from "./RelatedQuestions.tsx";

/**
 * This is the legacy version of ChatMessages, which will be replaced by the new ChatMessages component family.
 *
 * This is deprecated because it has too much stuff that is specific to InsightsAgent, and we want to make a more generic version.
 *
 * @deprecated Use the new ChatMessages component family instead.
 */
export const LegacyChatMessages: React.FC<{
  prompt?: {
    brief: string;
    questions: string;
  };
  researchType?: string;
  researchStatus?: Research["status"];
  messages?: Message[];
  onActionClick?: (action: MessageAction) => void;
  featureFlags?: {
    enableRelatedQuestions?: boolean;
    enableAppliedFilters?: boolean;
    enableNumberedCitations?: boolean;
    enableSplitCitations?: boolean;
  };
}> = (props) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ref.current) return;
    if (!ref.current.scrollTo) return;
    ref.current.scrollTo({
      top: ref.current.scrollHeight,
      behavior: "smooth",
    });
  }, [props.messages]);
  const FlaggedCitationComponent: CitationComponent = useMemo(() => {
    return (_props) => (
      <Citation
        {..._props}
        numbered={props.featureFlags?.enableNumberedCitations || false}
      />
    );
  }, [props.featureFlags?.enableNumberedCitations]);

  return (
    <div
      className={styles.ChatContainer}
      ref={ref}
      data-testid="chat-container"
    >
      {!props.researchStatus && <LoadingDots />}
      {props.prompt && (
        <div className={styles.MessageContainer + " " + styles.AlignRight}>
          <Card className={styles.FromUser + " " + styles.MessageCard}>
            <p>Research goal: {props.prompt.brief}</p>
            <p>Specific questions: {props.prompt.questions}</p>
            {props.researchType && (
              <p className={styles.ResearchTypeDisplay}>
                Created with {props.researchType} methodology
              </p>
            )}
          </Card>
          <ActionBar
            message={{
              sender: "user",
            }}
          />
        </div>
      )}
      {props.messages?.map((message) => (
        <MessageBubble key={message.id} message={message}>
          {props.featureFlags?.enableAppliedFilters && (
            <AppliedFilters appliedFilter={message.appliedFilters || {}} />
          )}
          <MarkdownWithCitations
            markdown={message.value || ""}
            citations={message.citations || []}
            references={message.references || []}
            CitationComponent={FlaggedCitationComponent}
            splitCitations={props.featureFlags?.enableSplitCitations || false}
          />
          {props.featureFlags?.enableRelatedQuestions &&
            !!message.actions?.filter(isRelatedQuestionAction)?.length && (
              <RelatedQuestions
                relatedQuestionActions={message.actions?.filter(
                  isRelatedQuestionAction,
                )}
                questionElement={(action) => (
                  <RelatedQuestions.Question
                    key={action.type + "-" + action.parameters.query}
                    onClick={(e) => {
                      e.preventDefault();
                      props.onActionClick?.(action);
                    }}
                    action={action}
                  />
                )}
              />
            )}
          {message.status === "optimistic" && message.sender === "agent" && (
            <LoadingDots />
          )}
        </MessageBubble>
      ))}
      {props.researchStatus === "processing_meta_analysis" ? (
        <LoadingCard />
      ) : null}
    </div>
  );
};
